import { Input } from "@components/ui/input";
import { Textarea } from "@components/ui/textarea";
import { useFieldArray, Controller, Control } from "react-hook-form";
import { Briefcase, Plus, Trash2 } from "lucide-react";
import { FormLabel } from "@ui/internal/components/label";
import { DndUpload } from "@ui/internal/components/formelement";

interface CTAContainerProps {
  control: Control<any>;
}

const CTAContainer = ({ control }: CTAContainerProps) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "join_cta",
  });

  return (
    <div className="w-full space-y-6 mt-10">
      <div className="space-y-4">
        {fields.map((field, index) => (
          <div
            key={field.id}
            className="p-5 rounded-md border bg-white shadow-sm relative group animate-in fade-in slide-in-from-top-2"
          >
            <button
              type="button"
              onClick={() => remove(index)}
              className="absolute top-5 right-5 text-gray-400 hover:text-red-500 transition-colors"
            >
              <Trash2 size={18} />
            </button>

            <div className="space-y-4">
              <div className="flex items-center gap-2 text-emerald-600 mb-2">
                <Briefcase size={20} />
                <h3 className="font-semibold text-lg">CTA Card</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <Controller
                    control={control}
                    name={`join_cta.${index}.title`}
                    render={({ field, fieldState: { error } }) => (
                      <div className="space-y-1">
                        <FormLabel label="Title" />
                        <Input
                          {...field}
                          placeholder="e.g. Join the Project Management Course Today."
                        />
                        {error && (
                          <span className="text-xs text-red-500">
                            {error.message}
                          </span>
                        )}
                      </div>
                    )}
                  />

                  <Controller
                    control={control}
                    name={`join_cta.${index}.description`}
                    render={({ field, fieldState: { error } }) => (
                      <div className="space-y-1">
                        <FormLabel label="Description" />
                        <Textarea
                          {...field}
                          placeholder="Enter a short description for the CTA section..."
                          className="min-h-[120px] resize-none"
                        />
                        {error && (
                          <span className="text-xs text-red-500">
                            {error.message}
                          </span>
                        )}
                      </div>
                    )}
                  />
                </div>

                <div className="space-y-1">
                  <FormLabel label="Image" />
                  <Controller
                    control={control}
                    name={`join_cta.${index}.image`}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <div>
                        <DndUpload
                          type="image"
                          onChange={onChange}
                          value={value}
                        />
                        {error && (
                          <span className="text-xs text-red-500">
                            {error.message}
                          </span>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {fields.length === 0 && (
        <button
          type="button"
          onClick={() => append({ title: "", description: "", image: "" })}
          className="w-full py-8 border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center text-gray-500 hover:border-emerald-500 hover:text-emerald-600 transition-colors bg-gray-50/50 hover:bg-emerald-50/50"
        >
          <Plus size={24} className="mb-2" />
          <span className="font-medium">Add CTA Card</span>
        </button>
      )}
    </div>
  );
};

export default CTAContainer;
